import { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";

/* Custom "Reply" icon from Figma — node 139-6636 */
const CustomReplyIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 20 20">
        <path
            d="M3.74992 10.4999L3.74982 3.75L5.24982 3.74998L5.24989 8.99993L12.8788 9L9.9165 6.03768L10.9772 4.97702L15.7501 9.75L10.9772 14.523L9.9165 13.4623L12.8788 10.5L3.74992 10.4999Z"
            fill="currentColor"
        />
    </SvgIcon>
);

/* Custom "Send" icon from Figma — node 139-6639 */
const CustomSendIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 20 20">
        <path
            d="M2.91667 1.12132C2.98688 1.12132 3.05595 1.13906 3.11747 1.17289L18.5028 9.63483C18.7045 9.74575 18.7781 9.99908 18.6672 10.2007C18.6291 10.27 18.5721 10.327 18.5028 10.3651L3.11747 18.827C2.91583 18.9379 2.66247 18.8643 2.55157 18.6628C2.51774 18.6012 2.5 18.5322 2.5 18.4619V1.53798C2.5 1.30787 2.68655 1.12132 2.91667 1.12132ZM4.16667 3.65207V9.16658H8.33333V10.8332H4.16667V16.3478L15.7083 9.99992L4.16667 3.65207Z"
            fill="currentColor"
        />
    </SvgIcon>
);

/**
 * "Continue conversation" input — Figma node 139-6631.
 *
 * Updated to use exact custom vectors from Figma for the icons.
 * Dimensions: border-radius 12px, border rgba(0,0,0,0.1),
 * padding 24px horizontal + 16px vertical, gap 16px.
 * Placeholder: "Continue conversation", 16px regular, color #898989.
 * Icons: Custom vectors, ~18px/20px, color #898989.
 */
export default function ContinueConversationInput({ onSubmit, disabled = false }) {
    const [value, setValue] = useState("");

    const handleSend = useCallback(() => {
        const trimmed = value.trim();
        if (!trimmed || disabled) return;
        onSubmit(trimmed);
        setValue("");
    }, [value, disabled, onSubmit]);

    return (
        <Box sx={{ mt: 0 }}>
            <TextField
                fullWidth
                size="small"
                placeholder="Continue conversation"
                value={value}
                disabled={disabled}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                    }
                }}
                multiline
                maxRows={4}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <CustomReplyIcon
                                sx={{
                                    fontSize: 18,
                                    color: "#898989",
                                }}
                            />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={handleSend}
                                disabled={disabled || !value.trim()}
                                sx={{
                                    color: "#898989", // Default matching Figma
                                    "&:hover": { color: "#3d3a37" }, // Darker on hover
                                    "&:disabled": { color: "#D1D5DB" },
                                }}
                            >
                                <CustomSendIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                    sx: {
                        borderRadius: "12px",
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 16,
                        lineHeight: "24px",
                        color: "#3d3a37",
                        backgroundColor: "#fff",
                        gap: "16px",
                        paddingLeft: "24px", // Ensure padding matches container
                        paddingRight: "16px", // Right padding slightly less due to icon button? Container says px: 24.
                        // Wait, TextField standard input adds padding. providing px in sx applies to the root.
                        // Figma: px-[24px].
                        // If I set px here, it works.
                        px: "24px",
                        py: "13px", // Vertical padding (16 - 3 each side = 6px total reduction)

                        // Override standard TextField height/padding
                        "& .MuiInputBase-input": {
                            p: 0, // Remove inner input padding to let container control it
                            height: "auto",
                        },

                        "& fieldset": {
                            borderColor: "rgba(0,0,0,0.1)",
                        },
                        "&:hover fieldset": {
                            borderColor: "rgba(0,0,0,0.15) !important",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "rgba(0,0,0,0.2) !important",
                            borderWidth: "1px !important",
                        },
                        "& input::placeholder, & textarea::placeholder": {
                            color: "#898989",
                            fontSize: 16,
                            fontWeight: 400,
                            opacity: 1,
                        },
                    },
                }}
            />
        </Box>
    );
}
